import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertContainer,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { WarningCircleIcon } from '@phosphor-icons/react';
import { formatCurrency } from '@erp/shared-utils';

interface CloseAgreementModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string, notes?: string) => void;
  expectedAmount: number;
  allocatedAmount: number;
  writeOffAmount: number;
  loading?: boolean;
}

const REASON_OPTIONS = [
  { value: 'immaterial', label: 'Immaterial variance - not worth pursuing' },
  { value: 'settled', label: 'Settled with supplier' },
  { value: 'fully_paid', label: 'Fully paid (variance within tolerance)' },
  { value: 'dispute', label: 'Dispute resolved - accepting variance' },
  { value: 'other', label: 'Other (explain in notes)' },
] as const;

const LARGE_WRITE_OFF_THRESHOLD = 5000;

export function CloseAgreementModal({
  open,
  onClose,
  onConfirm,
  expectedAmount,
  allocatedAmount,
  writeOffAmount,
  loading = false,
}: CloseAgreementModalProps) {
  const [reason, setReason] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setReason('');
      setNotes('');
    }
  }, [open]);

  const handleConfirm = () => {
    if (!reason) return;
    onConfirm(reason, notes || undefined);
  };

  const isLargeWriteOff = Math.abs(writeOffAmount) > LARGE_WRITE_OFF_THRESHOLD;
  const canSubmit = reason && !loading;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Close Agreement</DialogTitle>
          <DialogDescription>
            You are about to close the books on this agreement. The difference
            will be written off and this agreement will no longer appear in
            reconciliation reports.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Summary Card */}
          <Card className="bg-slate-50">
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground">Expected Rebate</div>
                  <div className="text-lg font-semibold">
                    {formatCurrency(expectedAmount)}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Allocated/Paid</div>
                  <div className="text-lg font-semibold">
                    {formatCurrency(allocatedAmount)}
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t">
                <div className="text-muted-foreground">Amount to Write Off</div>
                <div
                  className={`text-2xl font-bold ${
                    writeOffAmount < 0 ? 'text-red-600' : 'text-purple-600'
                  }`}
                >
                  {formatCurrency(Math.abs(writeOffAmount))}
                  <span className="text-sm ml-2 font-normal">
                    ({writeOffAmount < 0 ? 'Underpaid' : 'Overpaid'})
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Reason Selection */}
          <div>
            <Label htmlFor="reason">
              Reason for closure <span className="text-red-500">*</span>
            </Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger id="reason" className="mt-1">
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent>
                {REASON_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Additional notes (optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any context about this closure..."
              rows={3}
              className="mt-1"
            />
          </div>

          {/* Warning if large write-off */}
          {isLargeWriteOff && (
            <AlertContainer variant="destructive">
              <WarningCircleIcon className="h-4 w-4" />
              <AlertTitle>Large Write-Off</AlertTitle>
              <AlertDescription>
                This write-off exceeds $
                {LARGE_WRITE_OFF_THRESHOLD.toLocaleString()}. Please confirm
                this is correct.
              </AlertDescription>
            </AlertContainer>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canSubmit}>
            {loading ? 'Closing...' : 'Close Agreement'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
